import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, RefreshCw, Search, ShieldAlert, UserCog } from "lucide-react";
import type { AdminAuditAction } from "@shudian/contracts";
import { adminListAudit } from "../api/admin.js";
import { Field } from "../components/ui/Field.js";
import { Input } from "../components/ui/Input.js";
import { Button } from "../components/ui/Button.js";
import { Badge } from "../components/ui/Badge.js";
import { Table, TBody, THead, TR, TH, TD, EmptyRow } from "../components/ui/Table.js";
import { FullPageSpinner } from "../components/ui/Spinner.js";
import { Alert } from "../components/ui/Alert.js";
import { MetricCard, PageHeader, SectionHeader, SectionSurface } from "../components/PageScaffold.js";
import { CloudApiError, describeError } from "../lib/errors.js";
import { formatDateTime, formatNumber } from "../lib/format.js";

const ACTION_OPTIONS: AdminAuditAction[] = [
  "tenant.create",
  "tenant.profile.update",
  "tenant.license.update",
  "tenant.device.revoke",
  "tenant.user.create",
  "tenant.user.update",
  "tenant.user.role.update",
  "tenant.user.password.reset",
  "tenant.user.sessions.revoke",
  "system.public_collect_proxy.update",
  "cloud.auth.register.success",
  "cloud.auth.register.failure",
  "cloud.auth.login.success",
  "cloud.auth.login.failure",
  "cloud.auth.refresh.success",
  "cloud.auth.refresh.failure",
  "cloud.auth.logout",
  "cloud.device.activate.success",
  "cloud.device.activate.failure",
  "cloud.device.revoke",
  "cloud.device.revoke.failure",
  "cloud.entitlement_lease.issue.success",
  "cloud.entitlement_lease.issue.failure",
  "cloud.usage.quota.exceeded",
  "cloud.billing.checkout_session.create",
  "cloud.billing.webhook.processed",
  "cloud.billing.webhook.failure",
  "billing.settings.update",
  "cloud.rule_bundle.fetch.success",
  "cloud.rule_bundle.fetch.failure",
  "cloud.public_collect.product.success",
  "cloud.public_collect.product.failure",
  "cloud.public_collect.search.success",
  "cloud.public_collect.search.failure",
  "rule_bundle.publish",
  "desktop_update.publish",
  "desktop_update.upload_publish",
  "desktop_update.update",
  "desktop_update.enable",
  "desktop_update.disable",
  "cloud.sync.key_envelope.init",
  "cloud.sync.root_key.fetch",
  "cloud.sync.device.register",
  "cloud.sync.device.revoke",
  "cloud.sync.push",
  "cloud.sync.pull",
  "cloud.sync.blob.upload",
  "cloud.sync.blob.download",
  "cloud.sync.rejected",
  "cloud.admin.sync.device.revoke",
  "cloud.admin.sync.root_key.rotate"
];

const ACTION_LABELS: Record<AdminAuditAction, string> = {
  "tenant.create": "新增用户",
  "tenant.profile.update": "更新用户资料",
  "tenant.license.update": "更新授权信息",
  "tenant.device.revoke": "吊销授权设备",
  "tenant.user.create": "新增登录账号",
  "tenant.user.update": "更新登录账号",
  "tenant.user.role.update": "更新账号角色",
  "tenant.user.password.reset": "重置登录账号密码",
  "tenant.user.sessions.revoke": "踢出登录账号",
  "system.public_collect_proxy.update": "更新公开采集代理池",
  "cloud.auth.register.success": "用户注册成功",
  "cloud.auth.register.failure": "用户注册失败",
  "cloud.auth.login.success": "用户登录成功",
  "cloud.auth.login.failure": "用户登录失败",
  "cloud.auth.refresh.success": "刷新登录成功",
  "cloud.auth.refresh.failure": "刷新登录失败",
  "cloud.auth.logout": "用户退出登录",
  "cloud.device.activate.success": "设备激活成功",
  "cloud.device.activate.failure": "设备激活失败",
  "cloud.device.revoke": "用户吊销设备",
  "cloud.device.revoke.failure": "用户吊销设备失败",
  "cloud.entitlement_lease.issue.success": "签发授权成功",
  "cloud.entitlement_lease.issue.failure": "签发授权失败",
  "cloud.usage.quota.exceeded": "额度不足",
  "cloud.billing.checkout_session.create": "创建支付订单",
  "cloud.billing.webhook.processed": "处理支付回调",
  "cloud.billing.webhook.failure": "支付回调失败",
  "billing.settings.update": "更新支付设置",
  "cloud.rule_bundle.fetch.success": "获取规则包成功",
  "cloud.rule_bundle.fetch.failure": "获取规则包失败",
  "cloud.public_collect.product.success": "云端公开商品采集成功",
  "cloud.public_collect.product.failure": "云端公开商品采集失败",
  "cloud.public_collect.search.success": "云端公开搜索采集成功",
  "cloud.public_collect.search.failure": "云端公开搜索采集失败",
  "rule_bundle.publish": "发布规则包",
  "desktop_update.publish": "发布桌面更新",
  "desktop_update.upload_publish": "上传并发布桌面更新",
  "desktop_update.update": "更新桌面更新",
  "desktop_update.enable": "启用桌面更新",
  "desktop_update.disable": "停用桌面更新",
  "cloud.sync.key_envelope.init": "初始化云同步密钥（历史）",
  "cloud.sync.root_key.fetch": "拉取同步根密钥",
  "cloud.sync.device.register": "注册同步设备",
  "cloud.sync.device.revoke": "吊销同步设备",
  "cloud.sync.push": "上传同步数据",
  "cloud.sync.pull": "拉取同步数据",
  "cloud.sync.blob.upload": "上传同步密文",
  "cloud.sync.blob.download": "下载同步密文",
  "cloud.sync.rejected": "拒绝同步数据",
  "cloud.admin.sync.device.revoke": "管理员吊销同步设备",
  "cloud.admin.sync.root_key.rotate": "管理员轮换同步根密钥"
};

export function AuditPage() {
  const [tenantId, setTenantId] = useState("");
  const [action, setAction] = useState<AdminAuditAction | "">("");
  const [limit, setLimit] = useState<number>(50);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin", "audit", { tenantId, action, limit }],
    queryFn: () =>
      adminListAudit({
        tenantId: tenantId.trim() || undefined,
        action: action || undefined,
        limit
      })
  });

  const entries = data?.entries ?? [];
  const adminEntries = entries.filter((entry) => entry.actorType === "admin").length;
  const dangerEntries = entries.filter((entry) => actionTone(entry.action) === "danger").length;
  const writeEntries = entries.filter((entry) =>
    [
      "tenant.create",
      "tenant.profile.update",
      "tenant.license.update",
      "tenant.device.revoke",
      "tenant.user.password.reset",
      "system.public_collect_proxy.update",
      "billing.settings.update",
      "rule_bundle.publish",
      "desktop_update.publish",
      "desktop_update.upload_publish",
      "desktop_update.update",
      "desktop_update.enable",
      "desktop_update.disable"
    ].includes(entry.action)
  ).length;

  return (
    <div>
      <PageHeader
        eyebrow="审计与系统 / 审计日志"
        title="审计日志"
        description="查看管理员、客户登录和云端服务关键动作，排查授权、设备、采集、支付和发布问题。"
        actions={
          <Button variant="secondary" onClick={() => void refetch()} disabled={isFetching}>
            <RefreshCw className="h-4 w-4" />
            {isFetching ? "刷新中…" : "刷新"}
          </Button>
        }
      />

      <section className="mb-5 grid grid-cols-1 gap-3 md:grid-cols-3">
        <MetricCard label="当前记录" value={formatNumber(entries.length)} detail={`最多 ${formatNumber(limit)} 条`} icon={<ClipboardList className="h-4 w-4" />} />
        <MetricCard label="管理员操作" value={formatNumber(adminEntries)} detail="admin 发起动作" tone="info" icon={<UserCog className="h-4 w-4" />} />
        <MetricCard label="异常/失败" value={formatNumber(dangerEntries)} detail={`${formatNumber(writeEntries)} 条写操作记录`} tone={dangerEntries > 0 ? "danger" : "success"} icon={<ShieldAlert className="h-4 w-4" />} />
      </section>

      <SectionSurface className="mb-5">
        <SectionHeader title="筛选条件" description="全局审计保留完整技术信息；客户相关审计建议从客户详情进入查看。" />
        <div className="grid grid-cols-1 gap-4 px-4 py-4 md:grid-cols-4">
          <Field label="用户 ID" htmlFor="auditTenantId">
            <Input
              id="auditTenantId"
              placeholder="留空显示全部"
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
            />
          </Field>
          <Field label="动作" htmlFor="auditAction">
            <select
              id="auditAction"
              className="h-9 rounded border border-surface-border bg-surface-panel px-2.5 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/70"
              value={action}
              onChange={(event) => setAction(event.target.value as AdminAuditAction | "")}
            >
              <option value="">全部</option>
              {ACTION_OPTIONS.map((value) => (
                <option key={value} value={value}>
                  {ACTION_LABELS[value]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="数量上限" htmlFor="auditLimit" hint="1–200">
            <Input
              id="auditLimit"
              type="number"
              min={1}
              max={200}
              value={limit}
              onChange={(event) => setLimit(Number(event.target.value) || 50)}
            />
          </Field>
          <div className="flex items-end">
            <Button onClick={() => void refetch()} disabled={isFetching}>
              <Search className="h-4 w-4" />
              查询
            </Button>
          </div>
        </div>
      </SectionSurface>

      {isLoading ? (
        <FullPageSpinner />
      ) : isError ? (
        <Alert tone="danger">
          {error instanceof CloudApiError ? describeError(error.code) : "加载失败"}
        </Alert>
      ) : (
        <SectionSurface>
          <SectionHeader title={`审计记录 (${data?.entries.length ?? 0})`} />
          <Table className="rounded-none border-0">
            <THead>
              <TR>
                <TH>时间</TH>
                <TH>动作</TH>
                <TH>操作者</TH>
                <TH>用户 ID</TH>
                <TH>资源</TH>
                <TH>Metadata</TH>
              </TR>
            </THead>
            <TBody>
              {data && data.entries.length > 0 ? (
                data.entries.map((entry) => (
                  <TR key={entry.id}>
                    <TD className="whitespace-nowrap text-xs text-text-secondary">
                      {formatDateTime(entry.createdAt)}
                    </TD>
                    <TD>
                      <Badge tone={actionTone(entry.action)}>{ACTION_LABELS[entry.action]}</Badge>
                    </TD>
                    <TD>
                      <div className="text-sm text-text-primary">
                        {entry.actorEmail ?? entry.adminEmail ?? entry.actorId}
                      </div>
                      <div className="font-mono text-xs text-text-muted">
                        {actorTypeLabel(entry.actorType)} · {entry.actorId}
                      </div>
                    </TD>
                    <TD className="font-mono text-xs text-text-secondary">{entry.tenantId ?? "-"}</TD>
                    <TD>
                      <div className="text-sm text-text-primary">{entry.resourceType}</div>
                      <div className="font-mono text-xs text-text-muted">{entry.resourceId}</div>
                    </TD>
                    <TD>
                      <pre className="max-h-32 max-w-md overflow-auto rounded bg-surface-base px-2 py-1 font-mono text-xxs text-text-secondary">
                        {JSON.stringify(entry.metadata, null, 2)}
                      </pre>
                    </TD>
                  </TR>
                ))
              ) : (
                <EmptyRow colSpan={6} label="无匹配审计记录。" />
              )}
            </TBody>
          </Table>
        </SectionSurface>
      )}
    </div>
  );
}

function actionTone(action: AdminAuditAction): "success" | "warning" | "danger" | "info" | "neutral" {
  switch (action) {
    case "tenant.create":
      return "success";
    case "tenant.profile.update":
      return "info";
    case "tenant.license.update":
      return "info";
    case "tenant.device.revoke":
      return "danger";
    case "tenant.user.create":
      return "success";
    case "tenant.user.update":
    case "tenant.user.role.update":
      return "info";
    case "tenant.user.password.reset":
    case "tenant.user.sessions.revoke":
      return "warning";
    case "system.public_collect_proxy.update":
      return "info";
    case "cloud.auth.login.success":
    case "cloud.auth.refresh.success":
    case "cloud.device.activate.success":
    case "cloud.entitlement_lease.issue.success":
    case "cloud.rule_bundle.fetch.success":
    case "cloud.public_collect.product.success":
    case "cloud.public_collect.search.success":
      return "success";
    case "cloud.auth.login.failure":
    case "cloud.auth.refresh.failure":
    case "cloud.device.activate.failure":
    case "cloud.device.revoke.failure":
    case "cloud.entitlement_lease.issue.failure":
    case "cloud.rule_bundle.fetch.failure":
    case "cloud.public_collect.product.failure":
    case "cloud.public_collect.search.failure":
    case "cloud.usage.quota.exceeded":
      return "danger";
    case "cloud.auth.logout":
    case "cloud.device.revoke":
      return "warning";
    case "rule_bundle.publish":
    case "desktop_update.publish":
    case "desktop_update.upload_publish":
    case "desktop_update.update":
    case "desktop_update.enable":
    case "desktop_update.disable":
    case "cloud.sync.device.revoke":
    case "cloud.sync.rejected":
      return "warning";
    case "cloud.sync.key_envelope.init":
    case "cloud.sync.root_key.fetch":
    case "cloud.sync.device.register":
    case "cloud.sync.push":
    case "cloud.sync.pull":
    case "cloud.sync.blob.upload":
    case "cloud.sync.blob.download":
      return "info";
    default:
      return "neutral";
  }
}

function actorTypeLabel(
  actorType: "admin" | "tenant_owner" | "cloud_user" | "anonymous" | "system"
): string {
  switch (actorType) {
    case "admin":
      return "管理员";
    case "tenant_owner":
      return "账号所有者";
    case "cloud_user":
      // 历史 cloud_audit_entries 行的 actor_type 仍可能是 cloud_user，单账号化后等同于 tenant_owner。
      return "登录账号";
    case "anonymous":
      return "未登录";
    case "system":
      return "系统";
    default:
      return "未知";
  }
}
